import { Placement } from '@nextui-org/react';
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  CustomTooltip,
  Modal,
  ShimmerImage,
  ShimmerImageWrapper,
  ShimmerTextWrapper,
  TextTooltip,
} from '../../../../../../components';
import RelativeDate from '../../../../../../components/RelativeDate';
import { useWindowDimensions } from '../../../../../../hooks/useDimensions';
import { useIsOverflow } from '../../../../../../hooks/useIsOverflow';
import { ActivityUser } from '../../../../../../hooks/useListingSubscription';
import { ImgProxyResizer } from '../../../../../../utils/ImageProxyResizer';
import { getFullName, getInitials } from '../../../../../../utils/User';
import {
  AvatarContainer,
  AvatarsContainer,
  DateText,
  DetailsContentList,
  DetailsContentListItem,
  DetailsContentListItemLeftContainer,
  DetailsTitle,
  StyledAvatar,
  TooltipContentContainer,
  UserName,
  UserNameContainer,
} from './UsersActivityView.styles';

const MAX_AVATARS_COUNT = 4;

interface UsersActivityViewProps {
  loading?: boolean;
  users: ActivityUser[];
  maxAvatarsCount?: number;
  shimmersCount?: number;
  tooltipPlacement?: Placement;
}

const DetailsUserItem = ({
  loading,
  user,
  sizeAvatar,
}: {
  loading: boolean;
  user: ActivityUser;
  sizeAvatar: string;
}) => {
  const { t } = useTranslation();
  const nameRef = useRef();
  const isNameOverflow = useIsOverflow(nameRef);
  const userName = useMemo(() => getFullName(user?.firstName, user?.lastName), [user]);
  const resizedImage = new ImgProxyResizer(user?.imageUrl).largeAvatarSquaredUrl;

  return (
    <DetailsContentListItem>
      <DetailsContentListItemLeftContainer>
        <ShimmerImageWrapper loading={loading} $width={sizeAvatar} $height={sizeAvatar} $borderRadius="50%">
          <AvatarContainer $isConnected={user?.isConnected}>
            <StyledAvatar text={getInitials(user?.firstName, user?.lastName)} src={resizedImage} $bordered />
          </AvatarContainer>
        </ShimmerImageWrapper>
        <ShimmerTextWrapper loading={loading} $width="6rem" $height="0.75rem">
          <UserNameContainer>
            <TextTooltip tooltipText={userName} isDisabled={!isNameOverflow}>
              <UserName ref={nameRef}>{userName}</UserName>
            </TextTooltip>
          </UserNameContainer>
        </ShimmerTextWrapper>
      </DetailsContentListItemLeftContainer>
      <ShimmerTextWrapper loading={loading} $width="3.5rem" $height="0.75rem">
        <DateText>{user?.isConnected ? t('vdp.activeNow') : <RelativeDate date={user?.lastActiveAt} />}</DateText>
      </ShimmerTextWrapper>
    </DetailsContentListItem>
  );
};

const UsersActivityView = ({
  loading,
  users,
  maxAvatarsCount = MAX_AVATARS_COUNT,
  shimmersCount,
  tooltipPlacement,
  ...rest
}: UsersActivityViewProps) => {
  const { t } = useTranslation();
  const { isInTablet, isInMobile } = useWindowDimensions();
  const [isDetailsOpen, setDetailsOpen] = useState(false);

  if (!loading && !users?.length) return <></>;

  const sizeAvatar = isInMobile ? '2.5rem' : '2rem';
  const shimmersCountLocal = shimmersCount || maxAvatarsCount + 1;

  const renderDetailsItems = () => (
    <DetailsContentList>
      {(loading ? [...Array(shimmersCountLocal)] : users).map((user, index) => (
        <DetailsUserItem key={`detail-user-item-${index}`} loading={loading} user={user} sizeAvatar={sizeAvatar} />
      ))}
    </DetailsContentList>
  );

  const renderTooltipContent = () => (
    <TooltipContentContainer>
      <ShimmerTextWrapper loading={loading} $width="6rem" $height="0.75rem">
        <DetailsTitle>{t('vdp.seenBy')}</DetailsTitle>
      </ShimmerTextWrapper>
      {renderDetailsItems()}
    </TooltipContentContainer>
  );

  const renderAvatars = () => (
    <>
      {users.slice(0, maxAvatarsCount).map((user, index) => {
        const resizedImage = new ImgProxyResizer(user?.imageUrl).largeAvatarSquaredUrl;
        return (
          <AvatarContainer key={index} $isConnected={user?.isConnected}>
            <StyledAvatar text={getInitials(user.firstName, user.lastName)} src={resizedImage} $bordered />
          </AvatarContainer>
        );
      })}
      {users.length > maxAvatarsCount && <StyledAvatar squared text={`+${users.length - maxAvatarsCount}`} />}
    </>
  );

  const renderContent = () => (
    <AvatarsContainer {...rest} onClick={isInMobile ? () => setDetailsOpen(true) : undefined}>
      {loading
        ? [...Array(shimmersCountLocal)].map((_, index) => (
            <ShimmerImage key={index} $width={sizeAvatar} $height={sizeAvatar} $borderRadius="50%" />
          ))
        : renderAvatars()}
    </AvatarsContainer>
  );

  return !isInMobile ? (
    <CustomTooltip
      hideArrow
      placement={tooltipPlacement || (isInTablet ? 'bottomStart' : 'bottomEnd')}
      $minWidth="17.5rem"
      $maxWidth="17.5rem"
      content={<>{renderTooltipContent()}</>}
    >
      {renderContent()}
    </CustomTooltip>
  ) : (
    <>
      {renderContent()}
      <Modal
        title={t('vdp.seenBy')}
        mobileTitleMarginBottom="0.5rem"
        hideFooter
        open={isDetailsOpen}
        onClose={() => setDetailsOpen(false)}
      >
        {renderDetailsItems()}
      </Modal>
    </>
  );
};

export default UsersActivityView;
